import { RefObject, useEffect } from 'react';

type NodeOrRef = RefObject<Node> | Node | null;

const isRefObject = (nodeOrRef: NodeOrRef): nodeOrRef is RefObject<Node> => {
  return !!nodeOrRef && Object.prototype.hasOwnProperty.call(nodeOrRef, 'current');
};

/** Determines if a target DOM node is a child of ref */
const refContainsTarget = (nodeOrRef: NodeOrRef, target: Event['target']): boolean => {
  const node = isRefObject(nodeOrRef) ? nodeOrRef.current : nodeOrRef;
  return !!node?.contains(target as Node);
};

/** Call a callback function if focus shifts outside of a given list of refs
 *
 * Something problematic about onBlur is that it fires even when navigating
 * between elements of your component. It can also to fail to fire in certain
 * situations. Instead of relying on the 'blur' event, this hook looks at both
 * 'mousedown' and 'focus' to determine if focus is still on a child of any
 * of the provided refs.
 */
const useFocusLost = (callback: (event: Event) => void, refs: NodeOrRef[]): void => {
  useEffect(() => {
    /** Build a list of all of the root nodes for all refs.
     *
     * If refs contains nodes within the shadow DOM then we can't use document
     * as the root node.
     */
    const rootNodes = refs.reduce<Node[]>((rootNodes, nodeOrRef) => {
      const node = isRefObject(nodeOrRef) ? nodeOrRef.current : nodeOrRef;
      if (node) {
        const rootNode = node.getRootNode();
        if (!rootNodes.includes(rootNode)) {
          rootNodes.push(rootNode);
        }
      }
      return rootNodes;
    }, []);
    /** Executes the callback function if event.target is a child of any
     * of the refs.
     */
    const checkForFocus = async (event: Event): Promise<void> => {
      // Only call the callback if none of the given refs have focus
      if (refs.every((ref) => !refContainsTarget(ref, event.target))) {
        callback(event);
      }
    };

    rootNodes.forEach((rootNode) => {
      rootNode.addEventListener('mousedown', checkForFocus, true);
      // Also listen for focus on other elements, as we want to trigger save
      // while tabbing through inputs if the tab is out of the current form.
      rootNode.addEventListener('focus', checkForFocus, true);
    });

    return () => {
      rootNodes.forEach((rootNode) => {
        rootNode.removeEventListener('mousedown', checkForFocus, true);
        rootNode.removeEventListener('focus', checkForFocus, true);
      });
    };
  }, [callback, refs]);
};
export default useFocusLost;
